import React from 'react';
import useDebounce from 'Hooks/useDebounce';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useTranscriptContext } from 'Components/UI/Transcript/TranscriptContext';

function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function useTranscriptSegmentFinder(keyFormat) {
  const {
    selectedResource,
    selectedIndex,
  } = useTranscriptContext();
  const segments = selectedResource?.attr('segments').resources().list() || [];
  const { logEvent } = useEventTrackingLogEvent();
  const [state, setState] = React.useState({
    selected: null,
    currentFilter: '',
    matches: 0,
  });

  const scrollTo = id => document.getElementById(keyFormat(id))?.scrollIntoView();

  const next = () => {
    logEvent('Meeting Transcript: Clicked find next');
    if (state.selected >= state.matches - 1) {
      scrollTo(0);
      return setState({
        ...state,
        selected: 0,
      });
    }
    scrollTo(state.selected + 1);
    return setState({
      ...state,
      selected: state.selected + 1,
    });
  };

  const previous = () => {
    logEvent('Meeting Transcript: Clicked find previous');
    if (state.selected <= 0) {
      const nextSelected = state.matches - 1;
      scrollTo(nextSelected);
      return setState({
        ...state,
        selected: nextSelected,
      });
    }
    scrollTo(state.selected - 1);
    return setState({
      ...state,
      selected: state.selected - 1,
    });
  };

  const setFilter = useDebounce(value => {
    logEvent('Meeting Transcript: Searched for value');
    if (!value) {
      return setState({
        ...state,
        selected: null,
        currentFilter: '',
        matches: 0,
      });
    }
    const escapedValue = escapeRegex(value);
    const matches = segments.reduce((count, seg) => {
      const regex = new RegExp(escapedValue, 'ig');
      return count + (seg.attr('content').match(regex) || []).length;
    }, 0);
    scrollTo(0);
    return setState({
      ...state,
      selected: 0,
      currentFilter: escapedValue,
      matches,
    });
  }, 100, selectedIndex, selectedResource);

  React.useEffect(() => {
    if (!state.currentFilter) {
      return;
    }
    const matches = segments.reduce((count, seg) => {
      const regex = new RegExp(state.currentFilter, 'ig');
      return count + (seg.attr('content').match(regex) || []).length;
    }, 0);
    scrollTo(0);
    setState({
      ...state,
      selected: 0,
      matches,
    });
  }, [selectedIndex]);

  return {
    state,
    next,
    previous,
    setFilter,
  };
}
